import { useState, useCallback, useMemo, useEffect } from 'react';
import { useResponsiveValue } from 'braid-design-system';
import {
  createChunks,
  calculateTotalChunks,
  getChunkPosition,
  CAROUSEL_CONSTANTS,
  type CarouselItem,
  type AnimationDirection,
} from '../utils/carouselUtils';

interface UseCarouselStateProps {
  items: CarouselItem[];
  maxItemsToShow: number;
}

export const useCarouselState = ({
  items,
  maxItemsToShow,
}: UseCarouselStateProps) => {
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animatingToIndex, setAnimatingToIndex] = useState<number | null>(null);
  const [animationDirection, setAnimationDirection] =
    useState<AnimationDirection>('right');
  const [isResponsiveValueReady, setIsResponsiveValueReady] = useState(false);

  const responsiveValue = useResponsiveValue();
  const isMobile = responsiveValue({
    mobile: true,
    tablet: true,
    desktop: false,
  });

  // Track when the responsive value has been determined
  useEffect(() => {
    if (isMobile !== null && isMobile !== undefined) {
      setIsResponsiveValueReady(true);
    }
  }, [isMobile]);

  const chunkSize = isMobile ? 1 : maxItemsToShow;

  // Create chunks with stable keys
  const chunks = useMemo(
    () => createChunks(items, chunkSize),
    [items, chunkSize],
  );

  const totalChunks = useMemo(
    () => calculateTotalChunks(items.length, chunkSize),
    [items.length, chunkSize],
  );

  const { isFirst: isFirstChunk, isLast: isLastChunk } = useMemo(
    () => getChunkPosition(currentChunkIndex, totalChunks),
    [currentChunkIndex, totalChunks],
  );

  const animateAndChangeChunk = useCallback(
    (direction: AnimationDirection, newChunkIndex: number) => {
      if (isAnimating) {
        return;
      }

      setIsAnimating(true);
      setAnimationDirection(direction);
      setAnimatingToIndex(newChunkIndex);

      setTimeout(() => {
        setCurrentChunkIndex(newChunkIndex);
        setAnimatingToIndex(null);
        setIsAnimating(false);
      }, CAROUSEL_CONSTANTS.ANIMATION_DURATION);
    },
    [isAnimating],
  );

  return {
    // State
    currentChunkIndex,
    isAnimating,
    animatingToIndex,
    animationDirection,
    isMobile,
    isResponsiveValueReady,
    chunkSize,
    chunks,
    totalChunks,
    isFirstChunk,
    isLastChunk,

    // Actions
    animateAndChangeChunk,
  };
};
